/** Regras da partida (GDD, seção 2). Todos os tempos em segundos. */
export const MATCH = {
  durationSeconds: 8 * 60,
  suddenDeathSeconds: 60,
  goalPoints: 10,
  sparkCapturePoints: 30,
  sparkAppearsAtSeconds: 3 * 60,
  sparkSlowdownPerMinute: 0.05,
  stunSeconds: 2,
  stunDropMeters: 3,
  abilityCooldownSeconds: 12,
  hoopsPerSide: 3,
} as const;

/** Frequência da simulação e da rede (GDD, seção 9). */
export const NET = {
  simTickHz: 30,
  snapshotHz: 20,
  /** Máximo de ticks recuperados num único quadro, para a simulação não travar após uma pausa longa. */
  maxCatchUpTicks: 8,
} as const;

/** Entrada de controle é quantizada em inteiros de -127 a 127 antes de entrar na simulação e na rede. */
export const INPUT_AXIS_MAX = 127;

export type Position = "artilheiro" | "goleiro" | "batedor" | "cacador";

export const POSITIONS: Readonly<Record<Position, { label: string; perTeam: number }>> = {
  artilheiro: { label: "Artilheiro", perTeam: 3 },
  goleiro: { label: "Goleiro", perTeam: 1 },
  batedor: { label: "Batedor", perTeam: 2 },
  cacador: { label: "Caçador", perTeam: 1 },
};

export type BallKind = "pelota" | "petardo" | "faisca";

export const BALLS: Readonly<Record<BallKind, { label: string; count: number }>> = {
  pelota: { label: "Pelota", count: 1 },
  petardo: { label: "Petardo", count: 2 },
  faisca: { label: "Faísca", count: 1 },
};

export const PLAYERS_PER_TEAM = Object.values(POSITIONS).reduce((n, p) => n + p.perTeam, 0);

/** Andamento da partida (item 4.3). Tempos em segundos. */
export const MATCH_FLOW = {
  /** Contagem antes do início e depois do apito, com todos parados nas posições. */
  kickoffSeconds: 3,
  /** Comemoração do gol, com o jogo parado. Depois o goleiro de quem sofreu repõe a bola. */
  goalPauseSeconds: 3,
  /** O cobrador tem este tempo para arremessar o pênalti. */
  penaltySeconds: 8,
  /** Distância do cobrador até a linha dos aros no pênalti (m). */
  penaltyDistance: 20,
  /** Depois de cada parada (gol, pênalti) os Petardos voltam ao meio e esperam isto antes de caçar (s). */
  restartPetardoRestSeconds: 2,
} as const;

/** Faltas (GDD, seção 2). */
export const FOULS = {
  /**
   * Atropelo: quem vem mais rápido que isto (m/s) na direção de um adversário e bate nele comete empurrão.
   * Fica logo acima da velocidade máxima sem turbo do atleta mais rápido (FLIGHT.maxSpeed): só o atropelo no
   * turbo é falta; a trombada comum, que rouba a Pelota, continua legal.
   */
  pushSpeed: 19.5,
  /** Quem não é goleiro não pode ficar na própria área, perto dos aros, por mais que isto. */
  hoopBlockSeconds: 2,
  /** Distância de um aro que conta como bloquear (m). */
  hoopBlockDistance: 7,
} as const;

/** Posições de saída do time da casa (x < 0); o visitante é espelhado. Uma por vaga, na ordem de POSITIONS. */
export const KICKOFF_FORMATION: Readonly<Record<Position, readonly (readonly [number, number, number])[]>> = {
  artilheiro: [
    [-14, 10, -10],
    [-12, 10, 0],
    [-14, 10, 10],
  ],
  goleiro: [[-50, 15, 0]],
  batedor: [
    [-24, 12, -16],
    [-24, 12, 16],
  ],
  cacador: [[-32, 20, 0]],
};
